import { calculateTravelTimesAndDistancesBatch } from '../../services/routing/osrmClient';

export type ConsolidationInputRow = {
  originLat: number;
  originLon: number;
  targetName: string;
  targetLat: number;
  targetLon: number;
  targetType: string;
};

export type ConsolidationOutputRow = {
  consolidatedStopName: string;
  consolidatedStopLat: number;
  consolidatedStopLon: number;
  consolidatedTargetType: string;
  consolidatedStopDistanceCrowFliesKm: number;
  walkingDistanceToConsolidatedKm: number;
  walkingTimeToConsolidatedMin: number;
};

export type PairMetric = {
  crowKm: number;
  walkingKm: number;
  walkingMinutes: number;
  source: 'walking' | 'crow_fly_fallback';
};

type CandidateStop = {
  key: string;
  name: string;
  lat: number;
  lon: number;
  targetType: string;
};

export type ConsolidationMatrix = {
  rows: ConsolidationInputRow[];
  candidates: CandidateStop[];
  pairMetrics: Map<string, PairMetric>;
  candidatePairsBeforeCap: number;
  routedPairs: number;
};

export type ConsolidationConstraints = {
  distanceEnabled: boolean;
  maxWalkingMeters: number;
  timeEnabled: boolean;
  maxWalkingMinutes: number;
};

export type ConsolidationSummary = {
  originalUniqueTargets: number;
  consolidatedUniqueTargets: number;
  averageWalkingMinutes: number;
};

export type ConsolidationMatrixDiagnostics = {
  rowCount: number;
  candidateCount: number;
  pairCount: number;
  rowsWithNoPairs: number;
  coverageByMinutes: Record<number, { rowsWithEligibleCandidate: number; eligiblePairs: number }>;
  walkingSourcePairs: number;
  crowFlyFallbackPairs: number;
};

export type ConsolidationMatrixBuildOptions = {
  maxCandidatesPerRow?: number;
  maxPairs?: number;
  onProgress?: (completedPairs: number, totalPairs: number) => void;
  /** When true, skip OSRM walking batches and populate metrics from crow-fly distance only. */
  skipOsrmWalking?: boolean;
};

const WALKING_BATCH_SIZE = 60;
const EPSILON = 1e-9;
const BATCH_GAP_MS = 120;
const FALLBACK_WALKING_MIN_PER_KM = 12;
const MAX_PLAUSIBLE_WALKING_SPEED_KMH = 10;
const WALKING_PAIR_CACHE_MAX = 20000;
const COORD_KEY_PRECISION = 6;
const walkingPairMetricCache = new Map<string, Omit<PairMetric, 'crowKm'>>();

function haversineDistanceKm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
}

function toCandidateKey(row: ConsolidationInputRow): string {
  return `${row.targetName}|${row.targetLat.toFixed(7)}|${row.targetLon.toFixed(7)}|${row.targetType}`;
}

function toPairKey(rowIndex: number, candidateIndex: number): string {
  return `${rowIndex}:${candidateIndex}`;
}

function toWalkingPairCacheKey(fromLat: number, fromLon: number, toLat: number, toLon: number): string {
  return `${fromLat.toFixed(COORD_KEY_PRECISION)}|${fromLon.toFixed(COORD_KEY_PRECISION)}->${toLat.toFixed(
    COORD_KEY_PRECISION
  )}|${toLon.toFixed(COORD_KEY_PRECISION)}`;
}

function touchWalkingPairMetricCacheEntry(key: string, value: Omit<PairMetric, 'crowKm'>): void {
  if (walkingPairMetricCache.has(key)) {
    walkingPairMetricCache.delete(key);
  }
  walkingPairMetricCache.set(key, value);
  if (walkingPairMetricCache.size <= WALKING_PAIR_CACHE_MAX) return;
  const oldestKey = walkingPairMetricCache.keys().next().value as string | undefined;
  if (oldestKey) walkingPairMetricCache.delete(oldestKey);
}

export function clearConsolidationWalkingCacheForTests(): void {
  walkingPairMetricCache.clear();
}

function sleepCancellable(ms: number, signal: AbortSignal): Promise<void> {
  if (signal.aborted) {
    throw new DOMException('Aborted', 'AbortError');
  }
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException('Aborted', 'AbortError'));
    };
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

export async function buildConsolidationMatrix(
  rows: ConsolidationInputRow[],
  maxWalkingMetersForMatrix: number,
  signal: AbortSignal,
  options?: ConsolidationMatrixBuildOptions
): Promise<ConsolidationMatrix> {
  const candidateByKey = new Map<string, CandidateStop>();
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i]!;
    const key = toCandidateKey(row);
    if (!candidateByKey.has(key)) {
      candidateByKey.set(key, {
        key,
        name: row.targetName,
        lat: row.targetLat,
        lon: row.targetLon,
        targetType: row.targetType,
      });
    }
  }
  const candidates = Array.from(candidateByKey.values());
  const pairMetrics = new Map<string, PairMetric>();
  const maxKm = Math.max(0, maxWalkingMetersForMatrix) / 1000;

  const pairs: Array<{ rowIndex: number; candidateIndex: number; crowKm: number }> = [];
  const maxCandidatesPerRow = Math.max(1, options?.maxCandidatesPerRow ?? 18);
  const maxPairs = Math.max(100, options?.maxPairs ?? 8000);
  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i]!;
    const rowCandidates: Array<{ candidateIndex: number; crowKm: number }> = [];
    for (let j = 0; j < candidates.length; j += 1) {
      const c = candidates[j]!;
      const crowKm = haversineDistanceKm(row.originLat, row.originLon, c.lat, c.lon);
      if (crowKm <= maxKm + EPSILON) {
        rowCandidates.push({ candidateIndex: j, crowKm });
      }
    }
    rowCandidates.sort((a, b) => a.crowKm - b.crowKm);
    const selected = rowCandidates.slice(0, maxCandidatesPerRow);
    for (let k = 0; k < selected.length; k += 1) {
      const chosen = selected[k]!;
      pairs.push({ rowIndex: i, candidateIndex: chosen.candidateIndex, crowKm: chosen.crowKm });
    }
  }
  const candidatePairsBeforeCap = pairs.length;

  if (pairs.length > maxPairs) {
    pairs.sort((a, b) => a.crowKm - b.crowKm);
    pairs.length = maxPairs;
  }
  const routedPairs = pairs.length;

  if (options?.skipOsrmWalking) {
    for (let p = 0; p < pairs.length; p += 1) {
      const pair = pairs[p]!;
      const metric: PairMetric = {
        crowKm: pair.crowKm,
        walkingKm: pair.crowKm,
        walkingMinutes: pair.crowKm * FALLBACK_WALKING_MIN_PER_KM,
        source: 'crow_fly_fallback',
      };
      pairMetrics.set(toPairKey(pair.rowIndex, pair.candidateIndex), metric);
    }
    options?.onProgress?.(pairs.length, pairs.length);
    return { rows, candidates, pairMetrics, candidatePairsBeforeCap, routedPairs };
  }

  for (let offset = 0; offset < pairs.length; offset += WALKING_BATCH_SIZE) {
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    const chunk = pairs.slice(offset, offset + WALKING_BATCH_SIZE);
    const uncached: Array<{
      pair: { rowIndex: number; candidateIndex: number; crowKm: number };
      cacheKey: string;
      segment: {
        fromStop: { id: string; name: string; coordinates: { lat: number; lng: number } };
        toStop: { id: string; name: string; coordinates: { lat: number; lng: number } };
      };
    }> = [];
    for (let k = 0; k < chunk.length; k += 1) {
      const pair = chunk[k]!;
      const row = rows[pair.rowIndex]!;
      const cand = candidates[pair.candidateIndex]!;
      const cacheKey = toWalkingPairCacheKey(row.originLat, row.originLon, cand.lat, cand.lon);
      const cachedMetric = walkingPairMetricCache.get(cacheKey);
      if (cachedMetric) {
        pairMetrics.set(toPairKey(pair.rowIndex, pair.candidateIndex), {
          crowKm: pair.crowKm,
          walkingKm: cachedMetric.walkingKm,
          walkingMinutes: cachedMetric.walkingMinutes,
          source: cachedMetric.source,
        });
        touchWalkingPairMetricCacheEntry(cacheKey, cachedMetric);
        continue;
      }
      uncached.push({
        pair,
        cacheKey,
        segment: {
          fromStop: {
            id: `walk-from-${offset + k}`,
            name: `walk-from-${offset + k}`,
            coordinates: { lat: row.originLat, lng: row.originLon },
          },
          toStop: {
            id: `walk-to-${offset + k}`,
            name: `walk-to-${offset + k}`,
            coordinates: { lat: cand.lat, lng: cand.lon },
          },
        },
      });
    }
    if (!uncached.length) {
      options?.onProgress?.(Math.min(offset + chunk.length, pairs.length), pairs.length);
      continue;
    }
    const segments = uncached.map((entry) => entry.segment);

    try {
      const walked = await calculateTravelTimesAndDistancesBatch(segments, 'walking', signal);
      for (let i = 0; i < uncached.length; i += 1) {
        const { pair, cacheKey } = uncached[i]!;
        const result = walked[i];
        const walkingMeters = result?.distanceMeters ?? 0;
        const walkingKm = walkingMeters > 0 ? walkingMeters / 1000 : pair.crowKm;
        const reportedMinutes =
          result?.travelTimeMinutes && Number.isFinite(result.travelTimeMinutes) ? result.travelTimeMinutes : null;
        const reportedSpeedKmh =
          reportedMinutes && reportedMinutes > 0 ? walkingKm / (reportedMinutes / 60) : Number.POSITIVE_INFINITY;
        const useFallback =
          !reportedMinutes || !Number.isFinite(reportedSpeedKmh) || reportedSpeedKmh > MAX_PLAUSIBLE_WALKING_SPEED_KMH;
        const walkingMinutes = useFallback ? walkingKm * FALLBACK_WALKING_MIN_PER_KM : reportedMinutes;
        const metric: PairMetric = {
          crowKm: pair.crowKm,
          walkingKm,
          walkingMinutes,
          source: !useFallback && walkingMeters > 0 ? 'walking' : 'crow_fly_fallback',
        };
        pairMetrics.set(toPairKey(pair.rowIndex, pair.candidateIndex), metric);
        touchWalkingPairMetricCacheEntry(cacheKey, {
          walkingKm: metric.walkingKm,
          walkingMinutes: metric.walkingMinutes,
          source: metric.source,
        });
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error;
      }
      for (let i = 0; i < uncached.length; i += 1) {
        const { pair, cacheKey } = uncached[i]!;
        const metric = {
          crowKm: pair.crowKm,
          walkingKm: pair.crowKm,
          walkingMinutes: pair.crowKm * FALLBACK_WALKING_MIN_PER_KM,
          source: 'crow_fly_fallback',
        } as const;
        pairMetrics.set(toPairKey(pair.rowIndex, pair.candidateIndex), metric);
        touchWalkingPairMetricCacheEntry(cacheKey, {
          walkingKm: metric.walkingKm,
          walkingMinutes: metric.walkingMinutes,
          source: metric.source,
        });
      }
    }

    if (offset + WALKING_BATCH_SIZE < pairs.length) {
      await sleepCancellable(BATCH_GAP_MS, signal);
    }
    options?.onProgress?.(Math.min(offset + chunk.length, pairs.length), pairs.length);
  }

  return { rows, candidates, pairMetrics, candidatePairsBeforeCap, routedPairs };
}

function isEligible(metric: PairMetric, constraints: ConsolidationConstraints): boolean {
  if (constraints.distanceEnabled) {
    const maxKm = Math.max(0, constraints.maxWalkingMeters) / 1000;
    if (metric.walkingKm > maxKm + EPSILON) return false;
  }
  if (constraints.timeEnabled) {
    if (metric.walkingMinutes > Math.max(0, constraints.maxWalkingMinutes) + EPSILON) return false;
  }
  return true;
}

export function applyConsolidationConstraints(
  matrix: ConsolidationMatrix,
  constraints: ConsolidationConstraints
): ConsolidationOutputRow[] {
  const { rows, candidates, pairMetrics } = matrix;
  const uncovered = new Set<number>(rows.map((_r, i) => i));
  const chosenByRow = new Map<number, { candidate: CandidateStop; metric: PairMetric }>();

  while (uncovered.size > 0) {
    let bestCandidateIndex = -1;
    let bestCoverage = -1;
    let bestAvgWalkKm = Number.POSITIVE_INFINITY;
    let bestAvgWalkMin = Number.POSITIVE_INFINITY;
    let bestCoveredRows: Array<{ rowIndex: number; metric: PairMetric }> = [];

    for (let j = 0; j < candidates.length; j += 1) {
      const coveredRows: Array<{ rowIndex: number; metric: PairMetric }> = [];
      const uncoveredRows = Array.from(uncovered);
      for (let i = 0; i < uncoveredRows.length; i += 1) {
        const rowIndex = uncoveredRows[i]!;
        const metric = pairMetrics.get(toPairKey(rowIndex, j));
        if (metric && isEligible(metric, constraints)) {
          coveredRows.push({ rowIndex, metric });
        }
      }
      if (!coveredRows.length) continue;
      const coverage = coveredRows.length;
      const avgWalkKm = coveredRows.reduce((sum, x) => sum + x.metric.walkingKm, 0) / coverage;
      const avgWalkMin = coveredRows.reduce((sum, x) => sum + x.metric.walkingMinutes, 0) / coverage;
      if (
        coverage > bestCoverage ||
        (coverage === bestCoverage && avgWalkKm + EPSILON < bestAvgWalkKm) ||
        (coverage === bestCoverage && Math.abs(avgWalkKm - bestAvgWalkKm) <= EPSILON && avgWalkMin + EPSILON < bestAvgWalkMin)
      ) {
        bestCoverage = coverage;
        bestAvgWalkKm = avgWalkKm;
        bestAvgWalkMin = avgWalkMin;
        bestCandidateIndex = j;
        bestCoveredRows = coveredRows;
      }
    }

    if (bestCandidateIndex < 0 || !bestCoveredRows.length) {
      const remaining = Array.from(uncovered);
      for (let i = 0; i < remaining.length; i += 1) {
        const rowIndex = remaining[i]!;
        const row = rows[rowIndex]!;
        const fallbackKm = haversineDistanceKm(row.originLat, row.originLon, row.targetLat, row.targetLon);
        chosenByRow.set(rowIndex, {
          candidate: {
            key: toCandidateKey(row),
            name: row.targetName,
            lat: row.targetLat,
            lon: row.targetLon,
            targetType: row.targetType,
          },
          metric: {
            crowKm: fallbackKm,
            walkingKm: fallbackKm,
            walkingMinutes: fallbackKm * FALLBACK_WALKING_MIN_PER_KM,
            source: 'crow_fly_fallback',
          },
        });
        uncovered.delete(rowIndex);
      }
      break;
    }

    for (let i = 0; i < bestCoveredRows.length; i += 1) {
      const entry = bestCoveredRows[i]!;
      chosenByRow.set(entry.rowIndex, {
        candidate: candidates[bestCandidateIndex]!,
        metric: entry.metric,
      });
      uncovered.delete(entry.rowIndex);
    }
  }

  return rows.map((_row, index) => {
    const selected = chosenByRow.get(index);
    if (!selected) {
      const row = rows[index]!;
      const km = haversineDistanceKm(row.originLat, row.originLon, row.targetLat, row.targetLon);
      return {
        consolidatedStopName: row.targetName,
        consolidatedStopLat: row.targetLat,
        consolidatedStopLon: row.targetLon,
        consolidatedTargetType: row.targetType,
        consolidatedStopDistanceCrowFliesKm: Number(km.toFixed(3)),
        walkingDistanceToConsolidatedKm: Number(km.toFixed(3)),
        walkingTimeToConsolidatedMin: Number((km * FALLBACK_WALKING_MIN_PER_KM).toFixed(2)),
      };
    }
    return {
      consolidatedStopName: selected.candidate.name,
      consolidatedStopLat: selected.candidate.lat,
      consolidatedStopLon: selected.candidate.lon,
      consolidatedTargetType: selected.candidate.targetType,
      consolidatedStopDistanceCrowFliesKm: Number(selected.metric.crowKm.toFixed(3)),
      walkingDistanceToConsolidatedKm: Number(selected.metric.walkingKm.toFixed(3)),
      walkingTimeToConsolidatedMin: Number(selected.metric.walkingMinutes.toFixed(2)),
    };
  });
}

export function summarizeConsolidation(outputs: ConsolidationOutputRow[], inputRows: ConsolidationInputRow[]): ConsolidationSummary {
  const originalUniqueTargets = new Set(
    inputRows.map((row) => `${row.targetName}|${row.targetLat}|${row.targetLon}|${row.targetType}`)
  ).size;
  const consolidatedUniqueTargets = new Set(
    outputs.map(
      (row) => `${row.consolidatedStopName}|${row.consolidatedStopLat}|${row.consolidatedStopLon}|${row.consolidatedTargetType}`
    )
  ).size;
  const averageWalkingMinutes =
    outputs.length > 0
      ? outputs.reduce((sum, row) => sum + row.walkingTimeToConsolidatedMin, 0) / outputs.length
      : 0;
  return {
    originalUniqueTargets,
    consolidatedUniqueTargets,
    averageWalkingMinutes: Number(averageWalkingMinutes.toFixed(2)),
  };
}

export function buildConsolidationMatrixDiagnostics(
  matrix: ConsolidationMatrix,
  minuteThresholds: number[]
): ConsolidationMatrixDiagnostics {
  const thresholds = minuteThresholds
    .map((value) => Math.max(0, Math.round(value)))
    .filter((value, index, arr) => arr.indexOf(value) === index)
    .sort((a, b) => a - b);
  const coverageByMinutes: Record<number, { rowsWithEligibleCandidate: number; eligiblePairs: number }> = {};
  for (let i = 0; i < thresholds.length; i += 1) {
    coverageByMinutes[thresholds[i]!] = { rowsWithEligibleCandidate: 0, eligiblePairs: 0 };
  }

  const rowEligibleByThreshold = new Map<number, Set<number>>();
  for (let i = 0; i < thresholds.length; i += 1) {
    rowEligibleByThreshold.set(thresholds[i]!, new Set<number>());
  }

  let walkingSourcePairs = 0;
  let crowFlyFallbackPairs = 0;
  matrix.pairMetrics.forEach((metric, pairKey) => {
    if (metric.source === 'walking') {
      walkingSourcePairs += 1;
    } else {
      crowFlyFallbackPairs += 1;
    }
    const rowIndex = Number(pairKey.split(':')[0] ?? '-1');
    for (let i = 0; i < thresholds.length; i += 1) {
      const minutes = thresholds[i]!;
      if (metric.walkingMinutes <= minutes + EPSILON) {
        coverageByMinutes[minutes]!.eligiblePairs += 1;
        rowEligibleByThreshold.get(minutes)!.add(rowIndex);
      }
    }
  });

  for (let i = 0; i < thresholds.length; i += 1) {
    const minutes = thresholds[i]!;
    coverageByMinutes[minutes]!.rowsWithEligibleCandidate = rowEligibleByThreshold.get(minutes)!.size;
  }

  const rowsWithAnyPair = new Set<number>();
  matrix.pairMetrics.forEach((_metric, pairKey) => {
    const rowIndex = Number(pairKey.split(':')[0] ?? '-1');
    rowsWithAnyPair.add(rowIndex);
  });

  return {
    rowCount: matrix.rows.length,
    candidateCount: matrix.candidates.length,
    pairCount: matrix.pairMetrics.size,
    rowsWithNoPairs: Math.max(0, matrix.rows.length - rowsWithAnyPair.size),
    coverageByMinutes,
    walkingSourcePairs,
    crowFlyFallbackPairs,
  };
}
